import { useState, type ReactNode } from 'react';
import { CalendarClock, CircleCheck, Eye, TriangleAlert } from 'lucide-react';
import { deadlineOverview, type DeadlineEntry, type DeadlineGroup } from '@autoday/core';
import { useStore } from '../store/appStore.ts';
import { useToday } from '../ui/hooks.ts';
import { Button, Panel } from '../ui/kit.tsx';
import { AgendaRow } from '../components/Agenda.tsx';
import { useUi } from '../App.tsx';
import { tr, useLang } from '../i18n/i18n.ts';
import { formatClock, formatDayMonth, weekdayName } from '../i18n/format.ts';
import { Temporal } from '@autoday/core';

const GROUP_TEXT: Record<Exclude<DeadlineGroup, 'done'>, { title: [string, string]; hint: [string, string]; tone: string }> = {
  review: {
    title: ['Behöver granskas', 'Needs review'],
    hint: ['Källan har ändrats eller saknades i senaste importen. Inget har raderats, flyttats eller klarmarkerats automatiskt – inte heller dina förberedelser.', 'The source has changed or was missing in the latest import. Nothing has been deleted, moved or marked done automatically – nor your preparation.'],
    tone: 'border-warn/50',
  },
  passed: {
    title: ['Datum passerat, inte markerade klara här', 'Date passed, not marked done here'],
    hint: ['Passerat betyder inte misslyckat. AutoDay vet inte om du agerat utanför appen – kontrollera och markera klar här om det stämmer.', 'Passed does not mean failed. AutoDay does not know whether you acted outside the app – check and mark it done here if that is right.'],
    tone: 'border-risk/50',
  },
  soon: { title: ['Närmar sig (7 dagar)', 'Coming up (7 days)'], hint: ['', ''], tone: '' },
  later: { title: ['Senare', 'Later'], hint: ['', ''], tone: '' },
};

/** Kort beskrivning av en planerad förberedelse: "tis 3 juni 09.00". */
export function prepWhen(p: DeadlineEntry['prep'][number]): string {
  const d = Temporal.PlainDate.from(p.date);
  const time = p.timing.kind === 'timed' ? ' ' + formatClock(Number(p.timing.start.slice(11, 13)), Number(p.timing.start.slice(14, 16))) : '';
  return `${weekdayName(d.dayOfWeek, true)} ${formatDayMonth(p.date)}${time}`;
}

function EntryRow({ e, today, tz }: { e: DeadlineEntry; today: string; tz: string }) {
  const ui = useUi();
  const { t } = useLang();
  const open = (intent?: 'prep') => ui.openItem(e.item, intent);
  const pendingPrep = e.prep.filter((p) => !p.done);
  let action: ReactNode = null;
  if (e.next === 'review')
    action = (
      <Button className="min-h-10 px-3" icon={<Eye size={16} aria-hidden />} onClick={() => open()}>
        {t('Granska', 'Review')}
      </Button>
    );
  else if (e.next === 'confirm')
    action = (
      <Button className="min-h-10 px-3" icon={<CircleCheck size={16} aria-hidden />} onClick={() => open()}>
        {t('Kontrollera', 'Check')}
      </Button>
    );
  else if (e.next === 'prepare')
    action = (
      <Button className="min-h-10 px-3" icon={<CalendarClock size={16} aria-hidden />} onClick={() => open('prep')}>
        {t('Hitta tid att förbereda', 'Find time to prepare')}
      </Button>
    );
  return (
    <li className="border-b border-border last:border-b-0">
      <AgendaRow item={e.item} today={today} tz={tz} showDate bare />
      <div className="flex flex-wrap items-center gap-2 px-3 pb-3 pl-[8.75rem] text-xs text-muted max-sm:pl-3">
        <span className="min-w-0 flex-1">
          {e.origin === 'personal'
            ? t('Egen aktivitet som du markerat som viktig.', 'Your own activity, marked as important.')
            : e.recurringSource
              ? t('Återkommande källdatum – planera förberedelser manuellt i posten.', 'Recurring source date – plan preparation manually in the item.')
              : pendingPrep.length
                ? t(`Förberedelse planerad: ${pendingPrep.map(prepWhen).join(', ')}`, `Preparation planned: ${pendingPrep.map(prepWhen).join(', ')}`)
                : e.prep.length
                  ? t('Förberedelsen är klarmarkerad.', 'The preparation is marked done.')
                  : e.group === 'done'
                    ? t('Klar här (lokal kvittering).', 'Done here (local check-off).')
                    : t('Ingen förberedelse planerad.', 'No preparation planned.')}
        </span>
        {action}
      </div>
    </li>
  );
}

/**
 * Deadline Center. Samma underlag (deadlineOverview i core) som Idag och räknarna: varje förekomst finns i
 * exakt en grupp, och länkade förberedelser visas under sin deadline i stället för som egna rader.
 */
export function ImportantView() {
  const { state } = useStore();
  const today = useToday();
  const { t } = useLang();
  const tz = state.settings.timeZone;
  const [horizon, setHorizon] = useState(120);
  const ov = deadlineOverview(state, today, horizon);
  const by = (g: DeadlineGroup) => ov.entries.filter((e) => e.group === g);
  const done = by('done');
  const empty = ov.entries.length === 0;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-5 lg:px-8">
      <header>
        <p className="mb-1 text-xs font-medium uppercase tracking-widest text-accent">{t('Deadline Center', 'Deadline Center')}</p>
        <h1 className="text-2xl font-semibold tracking-tight">{t('Viktigt', 'Deadlines')}</h1>
        <p className="text-sm text-muted">
          {t(
            'Deadlines är poster från källor som räknas som deadlines (SkatteFix-exporten som standard) och egna aktiviteter du markerat som viktiga. Vanliga importerade möten räknas inte. Taggar kommer från importkällan; dagar kvar räknas här.',
            'Deadlines are items from sources counted as deadlines (the SkatteFix export by default) and your own activities marked as important. Ordinary imported meetings do not count. Tags come from the import source; days left are counted here.',
          )}{' '}
          <a className="font-medium text-accent underline-offset-2 hover:underline" href="#/kalendrar">
            {t('Ändra under Mina kalendrar', 'Change under My calendars')}
          </a>
        </p>
      </header>

      <section aria-label={t('Översikt', 'Overview')} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(['review', 'passed', 'soon', 'later'] as const).map((g) => (
          <div key={g} className={`rounded-xl border bg-panel px-3 py-2 ${ov.counts[g] && (g === 'review' || g === 'passed') ? (g === 'review' ? 'border-warn/60' : 'border-risk/60') : 'border-border'}`}>
            <p className="text-2xl font-semibold tabular-nums">{ov.counts[g]}</p>
            <p className="text-xs text-muted">{t(...GROUP_TEXT[g].title)}</p>
          </div>
        ))}
      </section>

      {empty && (
        <Panel className="p-5">
          <p className="font-medium">{t('Inga deadlines just nu.', 'No deadlines right now.')}</p>
          <p className="mt-1 text-sm text-muted">
            {t(
              `Inget inom ${horizon} dagar. Importera SkatteFix-exporten under Import, eller markera en egen aktivitet med "Visa under Viktigt".`,
              `Nothing within ${horizon} days. Import the SkatteFix export under Import, or mark one of your own activities with "Show under Deadlines".`,
            )}
          </p>
        </Panel>
      )}

      {(['review', 'passed', 'soon', 'later'] as const).map((g) => {
        const list = by(g);
        if (!list.length && (g === 'review' || g === 'passed')) return null;
        const meta = GROUP_TEXT[g];
        if (empty) return null;
        return (
          <Panel key={g} className={`p-2 ${meta.tone}`} aria-labelledby={`dl-${g}`}>
            <h2 id={`dl-${g}`} className={`flex items-center gap-2 px-3 pt-2 text-sm font-semibold ${g === 'review' ? 'text-warn' : g === 'passed' ? 'text-risk' : 'text-muted'}`}>
              {(g === 'review' || g === 'passed') && <TriangleAlert size={16} aria-hidden />}
              {t(...meta.title)} ({list.length})
            </h2>
            {meta.hint[0] && <p className="px-3 text-xs text-muted">{t(...meta.hint)}</p>}
            {list.length ? (
              <ul className="mt-1">
                {list.map((e) => (
                  <EntryRow key={e.item.uid} e={e} today={today} tz={tz} />
                ))}
              </ul>
            ) : (
              <p className="px-3 py-4 text-sm text-muted">{g === 'soon' ? t('Inget de närmaste sju dagarna.', 'Nothing in the next seven days.') : t(`Inget mer inom ${horizon} dagar.`, `Nothing more within ${horizon} days.`)}</p>
            )}
            {g === 'later' && horizon < 400 && (
              <button className="m-2 min-h-11 rounded-lg px-3 text-sm font-medium text-accent hover:bg-raised" onClick={() => setHorizon(400)}>
                {t('Visa längre fram', 'Show further ahead')}
              </button>
            )}
          </Panel>
        );
      })}

      {done.length > 0 && (
        <details className="rounded-[var(--radius-panel)] border border-border bg-panel px-2 py-1">
          <summary className="min-h-11 cursor-pointer px-3 py-3 text-sm font-semibold text-muted">
            {t(`Klara här (${done.length})`, `Done here (${done.length})`)}
          </summary>
          <ul>
            {done.map((e) => (
              <EntryRow key={e.item.uid} e={e} today={today} tz={tz} />
            ))}
          </ul>
        </details>
      )}
      <p className="text-xs text-muted">{tr('AutoDay hjälper dig hålla koll men kan inte garantera att ingen deadline missas. Kontrollera viktiga datum mot originalkällan.', 'AutoDay helps you keep track but cannot guarantee that no deadline is missed. Check important dates against the original source.')}</p>
    </div>
  );
}
